import { Alert, AlertTitle, Box, Button, CircularProgress, Typography } from '@mui/material';
import { VyIcon } from '@weft/icons';
import { extract, purge } from '@weft/utils';
import React, { useEffect, useRef } from 'react';
import { useConfirmUser } from '../../../../hooks/requests/use-confirm-user';
import { useVyNavigate } from '../../../../hooks/use-vy-navigate';
import { VerificationCodeInputFields } from '../../users-components/VerificationCodeInputFields';

type RegisterConfirmationPageFormProps = {
  token: string;
};

export const RegisterConfirmationPageForm: React.FC<RegisterConfirmationPageFormProps> = ({ token }) => {
  const confirmUserMutation = useConfirmUser();
  const navigate = useVyNavigate();
  const refs = useInitializeInputs();

  const handleConfirm = async () => {
    const inputsElements = purge(extract(refs, 'current'));
    const code = inputsElements.map(({ value })=>value).join('');

    await confirmUserMutation.mutateAsync({
      token,
      confirmationCode: code,
    });

    navigate('/login');
  };

  return (
    <div className="w-[456px] p-8 flex flex-col gap-8">
      <VyIcon
        icon="Logo"
        className="text-transparent"
      />
      <Box>
        <Typography
          variant="h5"
          fontWeight={300}
        >
          Welcome to Vayu!
        </Typography>
        <Typography
          variant="body2"
          fontWeight={300}
          color="#1B3D5599"
          fontSize={14}
        >
          Please enter verification code to complete registration
        </Typography>
      </Box>

      <VerificationCodeInputFields refs={refs} />

      <Button
        variant="contained"
        fullWidth
        size="large"
        onClick={handleConfirm}
      >
        {confirmUserMutation.isLoading ?
          <CircularProgress
            size={24}
            color="inherit"
          /> : 'Confirm'}
      </Button>

      {confirmUserMutation.isError && (
        <Alert severity="error">
          <AlertTitle>Confirmation failed!</AlertTitle>
          Please ensure confirmation code is correct and try again.
        </Alert>
      )}
    </div>
  );
};

export function useInitializeInputs() {
  const firstInputRef = useRef<HTMLInputElement>(null);
  const secondInputRef = useRef<HTMLInputElement>(null);
  const thirdInputRef = useRef<HTMLInputElement>(null);
  const fourthInputRef = useRef<HTMLInputElement>(null);
  const fifthInputRef = useRef<HTMLInputElement>(null);
  const sixthInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const inputElements = purge([
      firstInputRef.current,
      secondInputRef.current,
      thirdInputRef.current,
      fourthInputRef.current,
      fifthInputRef.current,
      sixthInputRef.current,
    ]);

    const handleInputKeydownEvent = (inputIndex: number) => {
      return (event: KeyboardEvent)=>{
        const inputElement = event.target as HTMLInputElement;
        if (event.key === 'Backspace' && inputElement.value === '') {
          inputElements[Math.max(0, inputIndex - 1)].focus();
        }
      };
    };

    const handleInputEvent = (inputIndex: number) => {
      return (event: Event)=> {
        const inputElement = event.target as HTMLInputElement;

        const [first, ...rest] = inputElement.value;

        inputElement.value = first ?? '';
        const lastInputBox = inputIndex === inputElements.length - 1;
        const didInsertContent = first !== undefined;

        if (didInsertContent && !lastInputBox) {
          inputElements[inputIndex + 1].focus();
          inputElements[inputIndex + 1].value = rest.join('');
          inputElements[inputIndex + 1].dispatchEvent(new Event('input'));
        }
      };
    };

    inputElements.forEach((element, inputIndex)=> {
      element.addEventListener('keydown', handleInputKeydownEvent(inputIndex));
      element.addEventListener('input', handleInputEvent(inputIndex));
    });

    return () => {
      inputElements.forEach((element, index)=>{
        element.removeEventListener('keydown', handleInputKeydownEvent(index));
        element.removeEventListener('input', handleInputEvent(index));
      });
    };
  }, [firstInputRef, secondInputRef, thirdInputRef, fourthInputRef, fifthInputRef, sixthInputRef]);

  return [ 
    firstInputRef,
    secondInputRef,
    thirdInputRef,
    fourthInputRef,
    fifthInputRef,
    sixthInputRef,
  ];
}
